import { useEffect, useState } from "react";

import type { TabVisibility } from "@/lib/refresh-policy";

/**
 * Subscribe to the Page Visibility API. Returns `"visible"` or
 * `"hidden"`; updates on every `visibilitychange` event.
 *
 * We deliberately DO NOT listen to `focus` / `blur` here. The browser
 * considers a tab "visible" when another app is on top of the browser
 * window, even though the tab has no focus — and for the refresh
 * policy that IS the semantics we want (user can still glance at the
 * number through the other window). Focus-based triggers live in the
 * refresh coordinator as an additional signal, not as the primary
 * visibility source.
 *
 * SSR safety: the hook's initial return is `"visible"` when `document`
 * is unavailable, so it doesn't gate real behaviour during any
 * hydration pass. All refresh policy reads happen client-side.
 */
export function useTabVisibility(): TabVisibility {
  const [visibility, setVisibility] = useState<TabVisibility>(() => {
    if (typeof document === "undefined") return "visible";
    return document.visibilityState === "hidden" ? "hidden" : "visible";
  });

  useEffect(() => {
    if (typeof document === "undefined") return;
    const onChange = () => {
      setVisibility(
        document.visibilityState === "hidden" ? "hidden" : "visible"
      );
    };
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);

  return visibility;
}
